import type { JourneyCase } from "../dtos";

/**
 * §Multi-application: a lead (one CRM journey) may run SEVERAL Nawy Now applications at once. The lead page
 * shows one at a time; the URL's `caseUid` search param says which, so a deep link, the tab and the
 * above-the-tabs status banner all agree without a shared store.
 */
export const CASE_UID_PARAM = "caseUid";

/** Oldest first — "Application 1" is the first one started. The list row carries no id, so order by
 * `caseStartedAt` (ISO, compares lexicographically) with the uid as a stable tiebreak. */
export const sortJourneyCases = (rows: readonly JourneyCase[]): JourneyCase[] =>
  [...rows].sort(
    (left, right) =>
      (left.caseStartedAt ?? "").localeCompare(right.caseStartedAt ?? "") ||
      left.uid.localeCompare(right.uid)
  );

/** The case a lead page opens on when no (valid) `caseUid` is asked for: the newest ACTIVE application,
 * else the newest of any status — the same "ACTIVE wins" rule the single-case page always used. */
export const pickDefaultCase = (sorted: readonly JourneyCase[]): JourneyCase | null => {
  for (let index = sorted.length - 1; index >= 0; index -= 1) {
    if (sorted[index].status === "ACTIVE") return sorted[index];
  }
  return sorted[sorted.length - 1] ?? null;
};

/** The requested case when it belongs to this journey, otherwise the default. */
export const resolveSelectedCase = (
  sorted: readonly JourneyCase[],
  requestedUid: string | null
): JourneyCase | null =>
  (requestedUid ? sorted.find((row) => row.uid === requestedUid) : undefined) ??
  pickDefaultCase(sorted);

/** The switcher's human label for a lifecycle status. */
export const caseStatusLabel = (row: Pick<JourneyCase, "status" | "terminatedAt">): string => {
  if (row.terminatedAt) return "Terminated";
  switch (row.status) {
    case "ACTIVE":
      return "Active";
    case "COMPLETED":
      return "Completed";
    case "CANCELLED":
      return "Cancelled";
    default:
      return row.status;
  }
};
